
<template>
  <naj-modal
    ref="objectionRequestModal"
    :title="local.judicialFees.submitObjection"
    button-size="small"
    :button-outlined="false"
    :width="1200"
    :persistent="true"
    :close-button="true"
    show-footer-close-button
    :content-loaded="allObjectionReasons.length>0"
    @onClose="onModalClose( )"
  >
    <v-form ref="objectionRequestFromRef">
      <v-row>
        <v-col cols="12">
          <naj-input
            :type-id="formFieldType.selectbox"
            :value.sync="objectionRequest.objectionReasonId"
            :label="local.judicialFees.objectionReasons"
            :items="allObjectionReasons"
            item-text="name"
            item-value="id"
            validates="required"
            @change="objectionReasonChanged"
          ></naj-input>
          <span
            v-if="selectedReason && selectedReason.description && objectionRequest.objectionReasonId"
            class="description-hint"
          >{{ selectedReason.description }} </span>
        </v-col>
        <v-col
          v-if="showOtherReason"
          cols="12"
        >
          <naj-input
            :key="'oTherObjectionReason-Input'"
            :type-id="formFieldType.textarea"
            :label="local.judicialFees.oTherObjectionReason"
            :value.sync="objectionRequest.otherObjectionReason"
            validates="required"
          ></naj-input>
        </v-col>
        <v-col cols="12">
          <naj-input
            :key="'objectionDetails-Input'"
            :type-id="formFieldType.textarea"
            :label="local.judicialFees.objectionDetails"
            :value.sync="objectionRequest.objectionDetails"
            validates="required"
          ></naj-input>
        </v-col>
        <v-col cols="12">
          <div class="text-h6">
            {{ local.judicialFees.attachments }}
          </div>
          <attachment-list :attachments="uploadAttachments"></attachment-list>
        </v-col>
        <v-col cols="6">
          <load-local-attachment
            :max-file-size="maxFileSize"
            @fileSelected="fileSelected"
          >
          </load-local-attachment>
          <span
            v-if="selectedReason && selectedReason.attachmentDescription && objectionRequest.objectionReasonId"
            style="margin-top: 5px"
            class="description-hint"
          >{{ selectedReason.attachmentDescription }} </span>
        </v-col>
      </v-row>
      <naj-confirmation
        :show-modal="confirmationVisibility"
        :action-loading="loading"
        color="error"
        :title="local.judicialFees.confirmationTitle"
        :message="local.judicialFees.confirmationMessage"
        :ok-btn-label="local.common.yes"
        :close-btn-label="local.common.no"
        @ok="callSubmitObjectionRequest"
        @cancel="confirmationVisibility = false"
      ></naj-confirmation>
    </v-form>
    <template
      slot="actions"
      slot-scope="{ closeNajModal }"
    >
      <naj-button
        :loading="loading"
        @click="submit(closeNajModal)"
      >
        {{ local.common.save }}
      </naj-button>
    </template>
  </naj-modal>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
const { mapActions: mapJudicialFeesActions, mapState: mapJudicialFeesState } = createNamespacedHelpers('JudicialFeesModule');
const { mapActions: mapCasesActions } = createNamespacedHelpers('CasesModule');

import LoadLocalAttachment from '@/components/shared/attachment-modal/load-local-attachment.vue';
import AttachmentList from '@/modules/judicialFees/components/attachment-list.vue';

export default {
  name: 'ObjectionRequestModal',
  components: {
    LoadLocalAttachment,
    AttachmentList,
  },
  props: {
    billObjectKey: {
      default: null,
      type: String,
      required: false,
    },
    providerId: {
      default: null,
      type: Number,
      required: false,
    },
    modalVisibility: {
      default: false,
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      loading: false,
      otherReasonId: -1,
      maxFileSize: 5,
      objectionRequest: {},
      uploadAttachments: [],
      showOtherReason: false,
      confirmationVisibility: false,
      selectedReason: {},
      allObjectionReasons: [],
    };
  },
  watch: {
    providerId(newVal) {
      if (newVal) this.getObjectionReasonsByProviderId();
    },
  },
  mounted() {
    this.getObjectionReasonsByProviderId();
  },
  methods: {
    ...mapJudicialFeesActions(['getObjectionReasons', 'submitObjectionRequest']),
    ...mapCasesActions(['uploadAttachment']),
    getObjectionReasonsByProviderId() {
      this.loading = true;
      this.getObjectionReasons(this.providerId).then(({ data }) => {
        this.allObjectionReasons = [...data, {
          id: this.otherReasonId,
          name: this.local.judicialFees.other,
        }];
      })
        .catch(err => this.toaster.error(err.message))
        .finally(() => (this.loading = false));
    },
    objectionReasonChanged(value) {
      this.showOtherReason = value == this.otherReasonId;
      this.selectedReason = (this.allObjectionReasons.filter(a => a.id == value) || [])[0];
    },
    fileSelected({ selectedAttachment: attachment, selectedAttachmentKey }) {
      this.loading = true;
      const reader = new FileReader();
      reader.onloadend = () => {
        var b64 = reader.result.replace(/^data:.+;base64,/, '');

        this.uploadAttachment({
          classification: 0,
          documentMetadata: {
            caseId: 0,
            caseCode: 0,
            requestId: 0,
            category: attachment.category,
            fileCategory: attachment.category,
          },
          fileCategory: attachment.category,
          extension: attachment.extension,
          base64FileContent: b64,
        })
          .then(({ data }) => {
            const uploadedFile = {
              sharePointId: data,
              fileName: attachment.name,
              fileType: attachment.fileObject.type,
              extension: attachment.extension,
              attachmentKey: selectedAttachmentKey,
              fileCategory: attachment.category,
            };
            if (!this.uploadAttachments) { this.uploadAttachments = []; }
            this.uploadAttachments.push(uploadedFile);
            this.$set(this, 'uploadAttachments', this.uploadAttachments);
          })
          .catch(err => this.toaster.error(err.message))
          .finally(() => (this.loading = false));
      };
      reader.readAsDataURL(attachment.fileObject);
    },
    validateAttachment() {
      return (this.uploadAttachments && this.uploadAttachments.length > 0 && this.uploadAttachments.length <= 2);
    },
    submit() {
      this.$refs.objectionRequestFromRef.$validator.validateAll()
        .then((valid) => {
          if (valid) {
            if (this.validateAttachment()) {
              this.confirmationVisibility = true;
            } else {
              this.toaster.error(this.local.judicialFees.attachmentValidationMessage);
            }
          }
        });
    },
    callSubmitObjectionRequest() {
      this.loading = true;
      const objectionReasonId = this.objectionRequest.objectionReasonId == this.otherReasonId ? null : this.objectionRequest.objectionReasonId;
      const oTherObjectionReason = this.objectionRequest.objectionReasonId == this.otherReasonId ? this.objectionRequest.oTherObjectionReason : null;
      const objectionRequestPayload = {
        ...this.objectionRequest,
        objectionReasonId,
        oTherObjectionReason,
        billObjectKey: this.billObjectKey,
        attachments: this.uploadAttachments,
      };
      this.submitObjectionRequest(objectionRequestPayload)
        .then(() => {
          this.confirmationVisibility = false;
          this.loading = false;
          this.closeModal();
          this.toaster.success(this.local.judicialFees.objectionRequestSuccessMessage);
        })
        .catch(() => {
          this.loading = false;
        }).finally(() => {
          this.loading = false;
        });
    },
    showModal() {
      this.$refs.objectionRequestModal.show();
    },
    closeModal() {
      this.$refs.objectionRequestModal.close();
    },
    onModalClose() {
      this.$set(this, 'objectionRequest', {});
      this.$set(this, 'uploadAttachments', []);
      this.$refs.objectionRequestFromRef.$validator.reset();
      this.$emit('objectionRequestModalClose');
      this.objectionRequest = {};
      this.showOtherReason = false;
      this.uploadAttachments = [];
      this.confirmationVisibility = false;
      this.loading = false;

    },
  },
};
</script>

<style lang="scss" scoped>
.header-text {
  color: var(--app-primary-color);
}

.text-title {
  font: normal normal bold 20px/31px Almarai;
}

.card-boarder {
  border: 2px solid #dbdbdb;
}

.description-hint {
  font-size: 12px;
  color: #8e98a1;
  display: block;
}
</style>
